import { message, Tooltip, Select } from "antd";
import { useEffect, useState } from "react";
import { backendGetRequest, backendPostWithoutRefresh, backendPatchWithoutRefresh, backendGetRequestWithCache } from "../../../helpers/callBackend";
import TableWithLoadingWrapper from "../../../components/generic/dataGrid";
import GenericDropdown from "../../../components/generic/genericDropdown";
import GenericDeleteModal from "../../../components/buttons/genericDeleteModal";
import { downloadPDF, downloadCSV, downloadTXT } from "../../../helpers/downloadPDF";
import LoadingButton from '@mui/lab/LoadingButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import IconButton from '@mui/material/IconButton';
import AddOrEditReportButton from "./AddOrEditReportButton";
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import HeaderBox from "../../../components/generic/arpariBoxes/headerBox";
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from "@mui/material/Typography";
import Switch from '@mui/material/Switch';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import OuterBox from "../../../components/generic/arpariBoxes/OuterBox";
import BankAccountMultiView from "../../../components/bankAccount/multiView";
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import GenericModal from "../../../components/buttons/genericModal";
import TransactionDownloadConfigModal from "../../../components/bankAccount/transactions/transactionsDownloadConfigModal";
import { DatePicker, Form } from 'antd';
import moment from "moment";
import {
	groupingTypes,
	typeFilter,
	groupingTypeFilter,
	transactionGroupingTypeFilter,
	groupKeyToLabelMap,
	transactionGroupKeyToLabelMap,
	isTransactionReportType,
	blockBai2Types,
} from "./constants";

const { Option } = Select;


function TabPanel(props) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`report-tabpanel-${index}`}
			aria-labelledby={`report-tab-${index}`}
			{...other}
		>
			{value === index && (
				<Box sx={{ pt: 2 }}>
					{children}
				</Box>
			)}
		</div>
	);
}

function a11yProps(index) {
	return {
		id: `report-tab-${index}`,
		'aria-controls': `report-tabpanel-${index}`,
	};
}

const ReportRunModal = ({
	isVisible,
	onClose,
	onSubmit,
	reportName,
	submitting
}) => {
	const handleSubmit = (values) => {
		onSubmit(values.selected_date);
		onClose();
	};

	const modalBody = (
		<Box sx={{ width: "100%" }}>
			<Form
				onFinish={handleSubmit}
				layout="vertical"
				initialValues={{
					selected_date: moment(),
				}}
			>
				<Form.Item
					name="selected_date"
					label="Select Date"
					rules={[{ required: true, message: "Please select a date!" }]}
				>
					<DatePicker
						placeholder="Select Date"
						disabledDate={(current) => current && current > moment()}
						style={{ width: "100%" }}

					/>
				</Form.Item>
				<Form.Item>
					<LoadingButton
						type="submit"
						sx={{ fontSize: 14, textTransform: 'none', width: '120px' }}
						disableElevation
						size="medium"
						variant="contained"
						color="primary"
						loadingPosition="start"
						loading={submitting}
					>
						Run Report
					</LoadingButton>
				</Form.Item>
			</Form>
		</Box>
	);

	return (
		<GenericModal
			visible={isVisible}
			handleCancelModal={onClose}
			modalTitle={`Run ${reportName}`}
			modalBody={modalBody}
			size="sm"
		/>
	);
};

const RunReportButton = ({ numAccounts, reportName, submitting, onRunReport }) => {
	const [isModalOpen, setIsModalOpen] = useState(false);

	const handleOpenModal = () => setIsModalOpen(true);
	const handleCloseModal = () => setIsModalOpen(false);

	const handleSubmit = (selectedDate) => {
		onRunReport(selectedDate);
	};

	return (
		<>
			<LoadingButton
				disabled={!numAccounts}
				onClick={handleOpenModal}
				sx={{ fontSize: 14, textTransform: 'none', width: '200px', marginBottom: '8px' }}
				disableElevation
				fullWidth
				size="small"
				variant="contained"
				color="primary"
				loadingPosition="start"
				loading={submitting}
			>
				Run Report
			</LoadingButton>

			{isModalOpen && (
				<ReportRunModal
					isVisible={isModalOpen}
					onClose={handleCloseModal}
					onSubmit={handleSubmit}
					reportName={reportName}
					submitting={submitting}
				/>
			)}
		</>
	);
};

const DownloadConfigModal = ({
	isVisible,
	onClose,
	onSubmit,
	reportTitle,
	reportDate
}) => {
	const [groupBy, setGroupBy] = useState("bank_account");
	const [units, setUnits] = useState("dollars");
	const [selectedColumns, setSelectedColumns] = useState([]);

	const groupByOptions = [
		{ value: "bank_account", label: "Bank Account" },
		{ value: "entity", label: "Entity" },
		{ value: "purpose", label: "Purpose" },
		{ value: "bank", label: "Bank" }
	];

	const unitsOptions = [
		{ value: "dollars", label: "$s" },
		{ value: "thousands", label: "$000s" },
		{ value: "millions", label: "$000,000s" }
	];

	const allColumns = {
		bank_account: [
			"Account Name",
			"Balance",
			"Bank",
			"Entity",
			"Arpari Account Description",
			"Account # Last 4",
			"Purpose",
			"Tags"
		],
		entity: ["Entity", "Balance"],
		purpose: ["Purpose", "Balance"],
		bank: ["Bank", "Balance"]
	};

	const lockedColumns = {
		bank_account: ["Account Name", "Balance"],
		entity: ["Entity", "Balance"],
		purpose: ["Purpose", "Balance"],
		bank: ["Bank", "Balance"]
	};

	const getColumns = () => allColumns[groupBy] || [];
	const getLockedColumns = () => lockedColumns[groupBy] || [];

	useEffect(() => {
		setSelectedColumns(getLockedColumns());
	}, [groupBy]);


	const handleColumnChange = (col) => {
		if (getLockedColumns().includes(col)) return;

		if (selectedColumns.includes(col)) {
			setSelectedColumns(selectedColumns.filter((c) => c !== col));
		} else {
			setSelectedColumns([...selectedColumns, col]);
		}
	};

	const handleSubmit = () => {
		// Exclude locked columns from download options
		const columnsWithoutLocked = selectedColumns.filter(
			(col) => !getLockedColumns().includes(col)
		);

		const downloadOptions = {
			groupBy,
			units,
			columns: columnsWithoutLocked
		};

		onSubmit(downloadOptions);
		onClose();
	};

	const modalBody = (
		<Box sx={{ width: "100%" }}>
			<Stack spacing={2}>
				{/* Group By */}
				<Box>
					<Typography sx={{ fontWeight: 500, mb: 1 }}>Group By</Typography>
					<Select
						value={groupBy}
						onChange={(value) => setGroupBy(value)}
						style={{ width: "100%" }}
					>
						{groupByOptions.map((option) => (
							<Option key={option.value} value={option.value}>
								{option.label}
							</Option>
						))}
					</Select>
				</Box>

				{/* Units */}
				<Box>
					<Typography sx={{ fontWeight: 500, mb: 1 }}>Units</Typography>
					<Select
						value={units}
						onChange={(value) => setUnits(value)}
						style={{ width: "100%" }}
					>
						{unitsOptions.map((option) => (
							<Option key={option.value} value={option.value}>
								{option.label}
							</Option>
						))}
					</Select>
				</Box>

				{/* Columns */}
				<Box>
					<Typography sx={{ fontWeight: 500, mb: 1, fontSize: 14 }}>Columns</Typography>
					<Stack direction="row" flexWrap="wrap">
						{getColumns().map((col) => (
							<Box key={col} sx={{ width: "50%" }}>
								<FormControlLabel
									control={
										<Checkbox
											checked={selectedColumns.includes(col)}
											onChange={() => handleColumnChange(col)}
											disabled={getLockedColumns().includes(col)}
										/>
									}
									label={col}
								/>
							</Box>
						))}
					</Stack>
				</Box>

				{/* Submit Button */}
				<Box sx={{ textAlign: "center", mt: 1 }}>
					<LoadingButton
						onClick={handleSubmit}
						sx={{ fontSize: 14, textTransform: "none", width: "120px" }}
						disableElevation
						fullWidth
						size="small"
						variant="contained"
						color="primary"
					>
						Submit
					</LoadingButton>
				</Box>
			</Stack>
		</Box>
	);

	return (
		<GenericModal
			visible={isVisible}
			handleCancelModal={onClose}
			modalTitle={`Download ${reportTitle} on ${reportDate?.split('T')[0] || ''}`}
			modalBody={modalBody}
			size="md"
		/>
	);
};


const ReportDetails = () => {
	const reportId = window.location.pathname.split("details/000")[1];
	const [reportData, setReportData] = useState({});
	const [submitting, setSubmitting] = useState(false);
	const [tabValue, setTabValue] = useState(0);

	const [savedReports, setSavedReports] = useState([]);
	const [savedReportsCount, setSavedReportsCount] = useState(-1);
	const [savedReportsLoading, setSavedReportsLoading] = useState(true);

	const [submittingReportCsv, setSubmittingReportCsv] = useState({});
	const [submittingReportPdf, setSubmittingReportPdf] = useState({});
	const [submittingReportBai2, setSubmittingReportBai2] = useState({});
	const [resettingReport, setResettingReport] = useState({});

	const [viewByInflowOutflow, setViewByInflowOutflow] = useState(false);

	const [reportType, setReportType] = useState(null);
	const [groupingType, setGroupingType] = useState(null);
	const [selectGroups, setSelectGroups] = useState(null);
	const [selectGroupFilter, setSelectGroupFilter] = useState([]);
	const [transactionGroupingType, setTransactionGroupingType] = useState(null);
	const [bankAccountsPerPage, setBankAccountsPerPage] = useState({});
	const [loadingBankAccounts, setLoadingBankAccounts] = useState(true);
	const [associatedBankAccounts, setAssociatedBankAccounts] = useState([]);
	const [pageCountExpected, setPageCountExpected] = useState(-1);
	const [pageCountReceived, setPageCountReceived] = useState(0);
	const [bankAccountInitialized, setBankAccountInitialized] = useState(false);
	const [selectedBankAccount, setSelectedBankAccount] = useState(0);
	const [groupingOptionsCache, setGroupingOptionsCache] = useState({});
	const [loadingGroupingOptions, setLoadingGroupingOptions] = useState({ 'BA': true, 'PU': true, 'EN': true, 'HI': true, 'AA': true });

	const [isBankAccountDownloadConfigModalVisible, setIsBankAccountDownloadConfigModalVisible] = useState(false);
	const [isTransactionsDownloadConfigModalVisible, setIsTransactionsDownloadConfigModalVisible] = useState(false);
	const [currentReport, setCurrentReport] = useState(null);
	const [currentDownloadType, setCurrentDownloadType] = useState(null);

	useEffect(() => {
		if (!groupingType) {
			setSelectGroupFilter([]);
			setSelectGroups([]);
			return;
		}
		if (groupingOptionsCache[groupingType]) {
			setSelectGroupFilter(groupingOptionsCache[groupingType]);
		} else {
			setSelectGroupFilter([]);
		}

		setSelectGroups([]);
	}, [groupingType, groupingOptionsCache]);

	useEffect(() => {
		groupingTypes.map((type) => {
			if (!groupingOptionsCache[type]) {
				setLoadingGroupingOptions(prev => ({ ...prev, [type]: true }));
				try {
					backendGetRequestWithCache(
						"/api/landlord/grouping-type-filter-options/",
						{ params: { grouping_type: type } },
						(cachedResponse) => {
							if (cachedResponse?.data?.data && !groupingOptionsCache[type]) {
								setGroupingOptionsCache(prev => ({ ...prev, [type]: cachedResponse.data.data }));
							}
						},
						(nonCachedResponse) => {
							if (nonCachedResponse?.data?.data) {
								setGroupingOptionsCache(prev => ({ ...prev, [type]: nonCachedResponse.data.data }));
							}
						},
						(error) => {
							console.error(`Error preloading grouping options for ${type}:`, error);
						}
					);
				} catch (error) {
					console.error(`Error preloading grouping options for ${type}:`, error);
				} finally {
					setLoadingGroupingOptions(prev => ({ ...prev, [type]: false }));
				}
			}
		});
	}, []);

	useEffect(() => {
		return () => {
			setLoadingGroupingOptions({});
		};
	}, []);

	const handleTabChange = (event, newValue) => {
		setTabValue(newValue);
	};

	const initiateDownload = (report, type) => {
		setCurrentReport(report);
		setCurrentDownloadType(type);
		if (allowDownloadOptions(type, reportData.type)) {
			if (isTransactionReport(reportData.type)) setIsTransactionsDownloadConfigModalVisible(true);
			else setIsBankAccountDownloadConfigModalVisible(true);
		} else {
			downloadHandler(report, type);
		}
	};

	useEffect(() => {
		savedReports.forEach((report) => {
			const csvExists = Object.keys(submittingReportCsv).includes(report.id);
			const pdfExists = Object.keys(submittingReportPdf).includes(report.id);
			const bai2Exists = Object.keys(submittingReportBai2).includes(report.id);
			const refreshExists = Object.keys(resettingReport).includes(report.id);

			if (!csvExists) {
				setSubmittingReportCsv(prevState => ({
					...prevState,
					[report.id]: false
				}));
			}
			if (!pdfExists) {
				setSubmittingReportPdf(prevState => ({
					...prevState,
					[report.id]: false
				}));
			}
			if (!bai2Exists) {
				setSubmittingReportBai2(prevState => ({
					...prevState,
					[report.id]: false
				}));
			}
			if (!refreshExists) {
				setResettingReport(prevState => ({
					...prevState,
					[report.id]: false
				}));
			}
		});
		savedReports.sort((a, b) => {
			let aMoment = moment.utc(a.run_date).format("YYYY-MM-DD HH:mm:ss.SSSSSS")
			let bMoment = moment.utc(b.run_date).format("YYYY-MM-DD HH:mm:ss.SSSSSS")
			if (aMoment > bMoment) return 1
			if (aMoment < bMoment) return -1
			return 0
		})
	}, [savedReports]);

	const isTransactionReport = (type) => {
		const transactionTypes = ["Prior Day Transactions", "MTD Transactions", "Prior Month Transactions", "Custom Period Transactions"];
		return transactionTypes.includes(type);
	};

	const allowDownloadOptions = (downloadType, reportType) => {
		const downloadOptionTypes = ["Current Balance", "Prior Day Balance", "Balance Diff (1 day)", "Balance Diff (1 week)", "Prior Month End Balance", "Balance as of Date"];
		return (downloadType === "pdf" || downloadType === "csv") && (downloadOptionTypes.includes(reportType) || isTransactionReport(reportType));
	}

	useEffect(() => {
		if (savedReportsCount < 0) return;
		setSavedReportsLoading(savedReportsCount > savedReports.length);
	}, [savedReportsCount, savedReports])

	useEffect(() => {
		backendGetRequest("/api/reports/", { params: { report_id: reportId } })
			.then((res) => {
				setReportData(res?.data?.data);
			});
	}, []);

	useEffect(() => {
		backendGetRequest("/api/reports/run/", { params: { report_id: reportId, page_index: 0 } })
			.then((res) => {
				const total_pages = res.data?.total_pages;
				setSavedReportsCount(res.data?.total_records);
				for (let page = 1; page <= total_pages; page++) {
					backendGetRequest("/api/reports/run/", { params: { report_id: reportId, page_index: 1 } })
						.then((res) => {
							setSavedReports(prevState => [...prevState, ...res.data?.data]);
						})
				}
			})
	}, []);

	const refreshExternalConnection = (bankAccount) => {
		backendGetRequest(
			"/api/external-connections/refresh-v2/",
			{
				params: {
					source: bankAccount?.account?.account_source,
					sourceGroupId: bankAccount?.account?.source_account?.source_group_id,
				}
			}
		).then(res => {
		})
	}

	useEffect(() => {
		backendGetRequest("/api/bank-account/", { params: { report_id: reportId, page_index: 0 } })
			.then((res) => {
				setPageCountExpected(prevState => res?.data?.total_pages);
				setBankAccountInitialized(true);
				for (let i = 1; i <= res?.data?.total_pages; i++) {
					let params = { report_id: reportId, page_index: i };
					backendGetRequestWithCache("/api/bank-account/", { params: params },
						(cachedResponse) => {
							if (cachedResponse?.data) {
								setBankAccountsPerPage(prevState => {
									if (!prevState[params.page_index]) {
										return {
											...prevState,
											[params.page_index]: cachedResponse?.data?.associated_bank_accounts,
										}
									}
									return prevState;
								});
							}
						},
						(nonCachedResponse) => {
							if (nonCachedResponse?.data) {
								setBankAccountsPerPage(prevState => ({
									...prevState,
									[params.page_index]: nonCachedResponse?.data?.associated_bank_accounts,
								}));
								setPageCountReceived(prevState => prevState + 1);
							}
						},
						() => { // error handling
							setPageCountExpected(prevState => prevState - 1);
						}
					);
				}
			})
			.catch(error => {
				console.error("Error fetching bank accounts:", error);
				setBankAccountInitialized(true);
				setLoadingBankAccounts(false);
			});
	}, [reportData]);

	useEffect(() => {
		if (bankAccountsPerPage && typeof bankAccountsPerPage === 'object') {
			const flattenedAccounts = Object.values(bankAccountsPerPage).flat();
			setAssociatedBankAccounts(flattenedAccounts);
		}
	}, [bankAccountsPerPage]);

	useEffect(() => {
		if (!bankAccountInitialized) return;
		const isLoading = pageCountExpected > pageCountReceived;
		setLoadingBankAccounts(prevState => isLoading);
	}, [bankAccountInitialized, pageCountExpected, pageCountReceived, associatedBankAccounts, associatedBankAccounts.length]);

	const onRunReport = (selectedDate) => {
		setSubmitting(true);
		const data = {
			report_id: reportId,
			run_date: moment
				.utc(selectedDate)
				.format("YYYY-MM-DD HH:mm:ss.SSSSSS"),
		};
		backendPostWithoutRefresh("/api/reports/run/", data, false, false)
			.then((res) => {
				setSavedReportsCount(prevState => prevState + 1);
				setSavedReports(prevState => [...prevState, res.data?.data]);
			})
			.finally(() => {
				setSubmitting(false);
			});
	};


	const resetReport = (reportRunId) => {
		setResettingReport(prevState => ({ ...prevState, [reportRunId]: true }));
		backendPatchWithoutRefresh("/api/reports/run/reset/", { report_run_id: reportRunId })
			.then((res) => {
				message.success("Report refreshed successfully");
			})
			.catch((error) => {
				message.error("Failed to refresh report");
				console.error("Error refreshing report:", error);
			})
			.finally(() => {
				setResettingReport(prevState => ({ ...prevState, [reportRunId]: false }));
			});
	}

	const deleteReport = (savedReportId) => {
		setSavedReportsCount(prevState => prevState - 1);
		setSavedReports(prevState => prevState.filter(savedReport => savedReport.id !== savedReportId));
	}

	const handleEditReport = (updatedReport) => {
		setReportData(updatedReport);
	};

	const downloadHandler = async (report, type, downloadOptions = {}) => {
		const setSubmittingReport =
			type === "csv" ? setSubmittingReportCsv :
				type === "pdf" ? setSubmittingReportPdf :
					setSubmittingReportBai2;

		setSubmittingReport(prevState => ({ ...prevState, [report.id]: true }));

		try {
			const initialRes = await backendGetRequest("/api/reports/run/result/", {
				params: {
					report_run_id: report.id,
					page_index: 0,
					type: type,
					view_by_inflow_outflow: viewByInflowOutflow,
				}
			});

			const totalPages = initialRes?.data?.total_pages || 1;

			const requests = [];
			for (let i = 1; i <= totalPages; i++) {
				requests.push(
					backendGetRequest("/api/reports/run/result/", {
						params: {
							report_run_id: report.id,
							page_index: i,
							type: type,
							view_by_inflow_outflow: viewByInflowOutflow,
						}
					})
				);
			}

			const responses = await Promise.all(requests);

			let report_run_data = responses.flatMap(res => res?.data?.data || []);

			const firstValidResponse = responses.find(res => res?.data?.data !== undefined)?.data?.data;

			if (firstValidResponse) {
				switch (true) {
					case Array.isArray(firstValidResponse):
						report_run_data = responses.flatMap(res => Array.isArray(res?.data?.data) ? res.data.data : []);
						break;

					case typeof firstValidResponse === 'object' && !Array.isArray(firstValidResponse):
						report_run_data = {};
						responses.forEach(res => {
							const data = res?.data?.data;
							if (typeof data === 'object' && !Array.isArray(data)) {
								Object.entries(data).forEach(([key, value]) => {
									if (Array.isArray(value) && Array.isArray(report_run_data[key])) {
										report_run_data[key] = [...report_run_data[key], ...value];
									} else {
										report_run_data[key] = value;
									}
								});
							}
						});
						break;
				}
			}

			// Download the combined report
			const downloadRes = await backendPostWithoutRefresh("/api/reports/run/download/", {
				report_run_id: report.id,
				report_data: report_run_data,
				type: type,
				group_by: downloadOptions.groupBy || undefined,
				units: downloadOptions.units || undefined,
				columns: downloadOptions.columns || undefined,
			});

			if (downloadRes?.status !== 204) {
				if (type === "csv") {
					downloadCSV(downloadRes?.data?.data, report.title);
				} else if (type === "pdf") {
					downloadPDF(downloadRes?.data, report.title);
				} else if (type === "bai2") {
					downloadTXT(downloadRes?.data?.data, `report-${report.title}-${report.date_created.split("T")[0]}`);
				}
			} else {
				message.info(downloadRes.statusText);
			}
		} catch (error) {
			console.error(`Error downloading ${type} report:`, error);
		} finally {
			setSubmittingReport(prevState => ({ ...prevState, [report.id]: false }));
		}
	};

	const handleModalSubmit = (options) => {
		if (currentReport && currentDownloadType) {
			downloadHandler(currentReport, currentDownloadType, options);
			setIsBankAccountDownloadConfigModalVisible(false);
			setIsTransactionsDownloadConfigModalVisible(false);
		}
	};

	const columns = [
		{
			field: 'run_date',
			headerName: 'Run Date',
			headerAlign: 'left',
			align: 'left',
			sort: 'desc',
			flex: 0.25,
			sortingOrder: ['desc', 'asc'],
			valueGetter: (_, params) => {
				return params?.run_date?.split('T')[0]
			},
			renderCell: (params) => (
				<Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
					<Typography variant="body1" alignItems="center">{params?.row?.run_date?.split('T')[0]}</Typography>
				</Box>

			),
		},
		{
			field: 'actions',
			headerName: '',
			flex: 2,
			// headerAlign: 'right',
			renderCell: (params) => (
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "end", height: "100%" }}>
					<Stack direction="row" display={"flex"} alignItems={"center"} spacing={1}>
						<IconButton
							color="primary"
							onClick={() => resetReport(params.row.id)}
							disabled={resettingReport[params.row.id]}
							sx={{ mr: 1 }}
						>
							<RefreshIcon
								className={resettingReport[params.row.id] ? "rotating" : ""}
								sx={{
									fontSize: 20,
									animation: resettingReport[params.row.id] ? 'spin 1s linear infinite' : 'none',
									'@keyframes spin': {
										'0%': { transform: 'rotate(0deg)' },
										'100%': { transform: 'rotate(360deg)' }
									}
								}}
							/>
						</IconButton>
						<LoadingButton
							onClick={() => initiateDownload(params.row, 'pdf')}
							loading={submittingReportPdf[params.row.id]}
							loadingPosition="start"
							sx={{ fontSize: 14, textTransform: 'none', width: '175px' }}
							disableElevation
							fullWidth
							size="small"
							variant="contained"
							color="primary"
						>
							Download PDF
						</LoadingButton>
						<LoadingButton
							onClick={() => initiateDownload(params.row, 'csv')}
							loading={submittingReportCsv[params.row.id]}
							loadingPosition="start"
							sx={{ fontSize: 14, textTransform: 'none', width: '175px' }}
							disableElevation
							fullWidth
							size="small"
							variant="contained"
							color="primary"
						>
							Download CSV
						</LoadingButton>
						{!blockBai2Types.includes(reportData.type) && (
							<LoadingButton
								onClick={() => initiateDownload(params.row, 'bai2')}
								loading={submittingReportBai2[params.row.id]}
								loadingPosition="start"
								sx={{ fontSize: 14, textTransform: 'none', width: '175px' }}
								disableElevation
								fullWidth
								size="small"
								variant="contained"
								color="primary"
							>
								Download BAI2
							</LoadingButton>
						)}
					</Stack>
				</Box>
			),
		},
		{
			field: 'delete',
			headerName: '',
			flex: 0.1,
			align: 'right',
			renderCell: (params) => (
				<GenericDropdown
					dropdownItems={[
						<GenericDeleteModal
							key={params.row.id}
							type="Report Run"
							title={`Are you sure you want to delete run date ${params.row?.run_date?.split('T')[0]}?`}
							URL="/api/reports/run/"
							deleteParams={{ report_id: params.row.id }}
							description={`By clicking Delete below you will no longer have this report for ${params.row?.run_date?.split('T')[0]} on your dashboard. If you do not want to proceed or unsure, please click X on the upper right.`}
							handleDeleteSuccess={() => deleteReport(params.row.id)}
							handleDeleteFailure={() => message.error(`Failed to delete the run date ${params.row?.run_date?.split('T')[0]}`)}
						/>
					]}
				/>
			),
		},
	];

	const formatHeaderBodyEntry = (title, val) => {
		return <Grid item>
			<Stack direciton="column">
				<Typography variant="overline" fontSize={12} sx={{ mb: 0, mr: 2 }} color="secondary" noWrap={true}>{title}</Typography>
				<Typography sx={{ mt: -1, mr: 3 }}>{val}</Typography>
			</Stack>
		</Grid>

	}

	return (
		<OuterBox>
			<HeaderBox
				title={reportData.name}
				titleFontSize={16}
				loading={reportData?.num_accounts}
				minHeight="100px"
				actions={
					<>
						{isTransactionReport(reportData.type) && (
							<Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
								<Typography sx={{ mr: 1, mb: 0.75 }}>View by Inflow/Outflow:</Typography>
								<Switch
									checked={viewByInflowOutflow}
									onChange={(e) => setViewByInflowOutflow(e.target.checked)}
									size="medium"
									sx={{ mb: 0.75 }}
								/>
							</Box>
						)}
						<Tooltip
							title={(reportData?.num_accounts || !reportData?.id) ? "" : "No accounts in this group. Cannot run report."}
						>
							<span>
								<RunReportButton
									numAccounts={reportData.num_accounts}
									reportName={reportData.name}
									submitting={submitting}
									onRunReport={onRunReport}
								/>
							</span>
						</Tooltip>

						<GenericDropdown
							dropdownItems={[
								<AddOrEditReportButton
									edit={true}
									name={reportData.name}
									type={typeFilter.find(item => item.label === reportData.type)?.value || reportData.type}
									groupingType={reportData.grouping_type}
									groupName={reportData.group_name}
									transactionGroupingType={reportData.transaction_grouping_type}
									startDate={reportData.start_date}
									endDate={reportData.end_date}
									numAccounts={reportData.num_accounts}
									reportId={reportId}
									onSubmit={handleEditReport}
									reportType={reportType}
									reportTypes={typeFilter}
									setReportType={setReportType}
									groupingTypeFilter={groupingTypeFilter}
									selectGroupFilter={selectGroupFilter}
									selectGroups={selectGroups}
									setGroupingType={setGroupingType}
									setSelectGroups={setSelectGroups}
									setTransactionGroupingType={setTransactionGroupingType}
									isTransactionReportType={isTransactionReportType}
									transactionGroupingTypeFilter={transactionGroupingTypeFilter}
									loadingGroupingOptions={!groupingType || loadingGroupingOptions[groupingType]}
								/>
							]}
						/>
					</>
				}
				body={<>
					<Grid
						container
						direction="row"
						spacing={6}
						sx={{
							justifyContent: "flex-start",
							alignItems: "center",
						}}
					>
						{formatHeaderBodyEntry("Report Type", reportData.type === "Custom Period Transactions" ? `${reportData.type} from ${reportData.start_date} to ${reportData.end_date}` : reportData.type)}
						{formatHeaderBodyEntry(groupKeyToLabelMap[reportData.grouping_type], reportData.group_name)}
						{isTransactionReportType[reportData.type] && (formatHeaderBodyEntry("Transaction Grouping Type", transactionGroupKeyToLabelMap[reportData.transaction_grouping_type]))}
						{formatHeaderBodyEntry("Date Created", reportData.date_created?.split('T')[0])}
					</Grid>
				</>}
			/>

			<Box sx={{ width: '100%' }}>
				<Tabs
					value={tabValue}
					onChange={handleTabChange}
					aria-label="report tabs"
					variant="fullWidth"
					sx={{ margin: 0, borderBottom: 1, borderColor: 'divider' }}
				>
					<Tab
						sx={{
							fontSize: 12,
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'center',
							alignItems: 'center',
							textTransform: 'capitalize',
							minHeight: 30,
							p: 0,
						}}
						icon={<ReceiptOutlinedIcon style={{ marginBottom: 0, marginRight: '10px' }} />}
						label="Report Runs"
						{...a11yProps(0)}
					/>
					<Tab
						sx={{
							fontSize: 12,
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'center',
							alignItems: 'center',
							textTransform: 'capitalize',
							minHeight: 30,
							p: 0,
						}}
						icon={<AccountBalanceOutlinedIcon style={{ marginBottom: 0, marginRight: '10px' }} />}
						label="Bank Accounts"
						{...a11yProps(1)}
						disabled={associatedBankAccounts.length === 0}
					/>
				</Tabs>

				<TabPanel value={tabValue} index={0}>
					<TableWithLoadingWrapper
						getRowId={(record) => record.id}
						pagination={{
							paginationModel: { pageSize: savedReports?.length && savedReports.length <= 10 ? savedReports.length : 10, page: 0 },
						}}
						pageSizeOptions={[5, 10, 25]}
						columns={columns}
						dataSource={[...savedReports]}
						tableLayout={"fixed"}
						style={{ width: "100%" }}
						sortModel={[
							{
								field: "run_date",
								sort: "desc",
							},
						]}
						scroll={{
							x: 600,
						}}
						loading={savedReportsLoading}
					/>
				</TabPanel>

				<TabPanel value={tabValue} index={1}>
					{associatedBankAccounts.length > 0 ? (
						<BankAccountMultiView
							allowActions={true}
							accountsInfo={associatedBankAccounts}
							setAccounts={setAssociatedBankAccounts}
							accountTypeTitle="Associated Bank Accounts"
							newAccountType="checking"
							accountOwnerAppName="report"
							loadingTable={loadingBankAccounts}
							setSelectedBankAccount={setSelectedBankAccount}
							reportId={reportId}
							displayPaymentsVerifiedColumn={false}
							reportsPage={true}
							refreshExternalConnection={refreshExternalConnection}
						/>
					) : (
						<Box sx={{ p: 3, textAlign: 'center' }}>
							<Typography color="text.secondary">
								No bank accounts associated with this report.
							</Typography>
						</Box>
					)}
				</TabPanel>
			</Box>
			<DownloadConfigModal
				isVisible={isBankAccountDownloadConfigModalVisible}
				onClose={() => setIsBankAccountDownloadConfigModalVisible(false)}
				onSubmit={handleModalSubmit}
				reportTitle={reportData.name}
				reportDate={currentReport?.date_created?.split('T')[0] || ''}
			/>
			<TransactionDownloadConfigModal
				reportTitle={reportData.name}
				reportDate={currentReport?.date_created?.split('T')[0] || ''}
				visible={isTransactionsDownloadConfigModalVisible}
				onClose={() => setIsTransactionsDownloadConfigModalVisible(false)}
				onSubmit={handleModalSubmit}
			/>
		</OuterBox>
	);
};

export default ReportDetails;
